import { Component, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AdminReportFilesService } from 'src/app/services/admin/admin-report-files.service';
import { DataService } from 'src/app/services/data.service';
import { FirestoreService } from 'src/app/services/firestore.service';
import { NewAuthService } from '../../../services/auth/new-auth-service.service';

@Component({
  selector: 'app-practice-invoices-report',
  templateUrl: './practice-invoices-report.component.html',
  styleUrls: ['./practice-invoices-report.component.scss'],
})
export class PracticeInvoicesReportComponent implements OnChanges, OnDestroy {
  @Input() clientId: string;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  reports: MatTableDataSource<any> = new MatTableDataSource();
  displayedColumns: string[] = ['invoice', 'url', 'claimUrl'];
  unsubscribe = new Subject<void>();

  private invoiceReports: any[] = [];
  private claimReports: any[] = [];

  constructor(
    private fsService: FirestoreService,
    private auth: NewAuthService,
    public ds: DataService,
    private adminReportFilesService: AdminReportFilesService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.clientId) {
      this.unsubscribe.next();
      this.unsubscribe.complete();
      this.getReports();
    }
  }

  monthYear(id: string): string {
    const [month, year] = id.split('_').map(Number);
    const monthName = this.ds.months[month - 1]?.viewValue.substring(0, 3) || '';
    return `${monthName} ${year}`;
  }

  getReports(): void {
    this.unsubscribe = new Subject<void>();
    if (!this.clientId) {
      this.invoiceReports = [];
      this.claimReports = [];
      this.mergeReports();
      return;
    }

    this.fsService
      .col('clients')
      .doc(this.clientId)
      .collection('monthly_invoice_reports')
      .valueChanges({ idField: 'id' })
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((data: any[]) => {
        this.invoiceReports = data;
        this.mergeReports();
      });

    this.fsService
      .col('clients')
      .doc(this.clientId)
      .collection('claim_history_reports')
      .valueChanges({ idField: 'id' })
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((data: any[]) => {
        this.claimReports = data;
        this.mergeReports();
      });
  }

  /** Claim history files are private in S3, so they open through a short-lived link. */
  openClaimReport(docId: string): void {
    this.adminReportFilesService.open({ type: 'claim-history', clientId: this.clientId, docId });
  }

  private mergeReports(): void {
    const reportMap = new Map<string, { invoice: string; url: string; claimUrl: string; idDoc: string }>();

    for (const doc of this.invoiceReports) {
      if (doc.filePath) {
        reportMap.set(doc.id, {
          invoice: this.monthYear(doc.id),
          url: doc.filePath,
          claimUrl: '',
          idDoc: doc.id,
        });
      }
    }

    for (const doc of this.claimReports) {
      if (doc.filePath && doc.status === 'completed') {
        const existing = reportMap.get(doc.id);
        if (existing) {
          existing.claimUrl = doc.filePath;
        } else {
          reportMap.set(doc.id, {
            invoice: this.monthYear(doc.id),
            url: '',
            claimUrl: doc.filePath,
            idDoc: doc.id,
          });
        }
      }
    }

    const merged = Array.from(reportMap.values())
      .filter((r) => r.url || r.claimUrl)
      .sort((a, b) => {
        const [monthA, yearA] = a.idDoc.split('_').map(Number);
        const [monthB, yearB] = b.idDoc.split('_').map(Number);
        return yearB - yearA || monthB - monthA;
      });

    this.reports.data = merged;
    this.reports.paginator = this.paginator;
  }

  openUrl(url: string): void {
    window.open(url, '_blank');
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
